import { ClassType, throwError, valuesOfMap } from '@nx-data/utilities';
import { DbEvents } from './events';
import { EventDispatcher } from './events/nx-events';
import { CreateModel, Entity } from './model';
import { NxTable } from './nx-table';

export class NxDatabase {
  // @ts-ignore
  private readonly entityTables: Map<ClassType<any>, NxTable<any>> = new Map();
  public readonly eventBus = new EventDispatcher<DbEvents>();

  public registerTable<T extends Entity>(classType: ClassType<T>, table: NxTable<T>) {
    this.checkIfTableIsInsertable(classType, table.tableName);
    this.entityTables.set(classType, table);
  }

  public createTable<T extends Entity>(classType: ClassType<T>, tableName: string): NxTable<T> {
    return new NxTable(tableName, classType, this);
  }

  private checkIfTableIsInsertable<T extends Entity>(classType: ClassType<T>, tableName: string): void | never {
    if (this.entityTables.has(classType)) {
      throw Error(
        `Tried to register multiple tables under the same class type ${classType.name}.
        Check if duplicates are registered or use a separate DB instance`
      );
    }

    for (const value of this.entityTables.values()) {
      if (value.tableName === tableName) {
        // throw Error(`Tried to register multiple tables under the same name ${tableName}`);
      }
    }
  }

  public getTable<T extends Entity>(classType: ClassType<T>): NxTable<T> {
    return this.entityTables.get(classType) ?? throwError(`No such table exists: ${classType.name}`);
  }

  public dumpDb(): Record<string, CreateModel<Entity>[]> {
    return valuesOfMap(this.entityTables).reduce((obj, table) => ({ ...obj, [table.tableName]: table.dumpTable() }), {});
  }

  public restoreDbFromDump(dump: Record<string, CreateModel<Entity>[]>) {
    let recordIntegrityCorrect = true;
    let emptynessIntegrityCorrect = true;

    for (const [, table] of this.entityTables) {
      if (!(table.tableName in dump)) {
        console.warn(`DB dump integrity is incorrect, table ${table.tableName} is missing from dump`);
        recordIntegrityCorrect = false;
      }

      if (table.hasAny()) {
        console.warn(
          `DB state integrity is incorrect, table ${table.tableName} already has records, dump restore is only safe for empty databases`
        );
        emptynessIntegrityCorrect = false;
      }
    }

    if (!recordIntegrityCorrect || !emptynessIntegrityCorrect) {
      console.warn('DB dump restore skipped');
      return;
    }

    for (const [, table] of this.entityTables) {
      table.restoreFromDump(dump[table.tableName] as CreateModel<Entity>[]);
    }
  }

  public dispose() {
    this.eventBus.dispose();
    for (const table of this.entityTables.values()) {
      table.dispose();
    }
  }
}
